import { useQuery } from '@apollo/client';
import { Skeleton } from '@mui/material';
import type React from 'react';
import AUDIO_IMAGE from 'src/assets/audio-image.png';
import PDF_IMAGE from 'src/assets/pdfImage.png';
import { BoxImageStyles, ImageWrapperStyles } from 'src/components/CommonStyles/ImageDiv/ImageDivStyles';
import ImageBox from 'src/components/ImageBox/ImageBox';
import PDFThumbnail from 'src/components/PDF/PDFThumbnail';
import VideoJs from 'src/components/VideoJs';
import {
  AUDIO,
  FD_INPOINT_SLUG,
  FD_MIMETYPE_SLUG,
  FD_NAME_SLUG,
  FD_TC_SLUG,
  IMAGE,
  MEDIA_ERROR,
  MEDIA_PATH,
  MXF,
  PDF,
  PROXY_FORMAT_SLUG,
  PROXY_OFFLINE,
  SHARED_MEDIA_PATH,
  THUMBNAIL_FORMAT_SLUG,
  THUMBNAIL_OFFLINE,
  VIDEO,
} from 'src/constants';
import { buildFormatsQ, buildSharedFormatsQ } from 'src/dynamoQueries/queryHelpers';
import { getFirstNestedWithSaneDefault, getMetadataValue } from 'src/helpers/common';
import { genMediaUrl } from 'src/helpers/generateCustomFields';
import { isVisualMode } from 'src/helpers/mode';
import { useMetaQ } from 'src/hooks/meta';
import viewStateVar from 'src/store/viewState';
import type Player from 'video.js/dist/types/player';
import type { FormatData, MarkerData, VideoJsOptions } from '../types';
import MediaUnavailable from './MediaUnavailable';

const FORMAT = 'FORMAT';
const META = 'METADATA';

const LoadingMedia: React.FC = () => {
  return (
    <ImageWrapperStyles>
      <BoxImageStyles>
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(255, 255, 255, 0.04)',
            '&::after': {
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)',
            },
          }}
        />
      </BoxImageStyles>
    </ImageWrapperStyles>
  );
};

interface MediaRouterComponentProps {
  itemId: string;
  itemObjectId?: string | null;
  setPlayer?: (player: Player) => void;
  shared?: boolean;
  videoJsProps?: { options?: VideoJsOptions };
  getVideoRef?: ((ref: HTMLVideoElement) => void) | null;
  marker?: MarkerData;
  akomiMediaCardIndex?: number;
  onImageClick?: () => void;
  // When true, image-type assets render the proxy (full-resolution) rendition
  // instead of the thumbnail. Use on detail/viewer pages; leave false (default)
  // for grid cards, list rows, playlists, etc. where the thumbnail is desired.
  fullResolution?: boolean;
}

interface FormatVariables {
  asset_Id?: string;
  asset_Items_ObjectId?: string;
  fmtType_Slug?: string;
}

interface MetaVariables {
  globalId?: string;
  objectId?: string;
  definitionSlugIn?: string[];
}

export const MediaRouterComponent: React.FC<MediaRouterComponentProps> = ({
  itemId,
  itemObjectId = null,
  setPlayer,
  shared = false,
  videoJsProps,
  getVideoRef = null,
  marker,
  fullResolution = false,
  ...props
}) => {
  const getVariables = (type: string): FormatVariables | MetaVariables => {
    if (type === FORMAT) {
      return itemObjectId ? { asset_Items_ObjectId: itemObjectId } : { asset_Id: itemId };
    }
    if (type === META) {
      return itemObjectId ? { objectId: itemObjectId } : { globalId: itemId };
    }
    return {};
  };

  const {
    data: proxyFormatData,
    loading,
    error,
  } = useQuery(shared ? buildSharedFormatsQ() : buildFormatsQ(), {
    skip: !itemId && !itemObjectId,
    variables: { ...getVariables(FORMAT), fmtType_Slug: PROXY_FORMAT_SLUG },
  });

  const proxyFormat: FormatData = getFirstNestedWithSaneDefault(proxyFormatData, [{}])[0];

  const {
    data: formatData,
    loading: loadingFormatData,
    error: formatDataError,
  } = useMetaQ(
    {
      skip: !proxyFormat?.id,
      variables: { globalId: proxyFormat?.id, definitionSlugIn: [FD_MIMETYPE_SLUG] },
    },
    shared,
  );

  const mimetype =
    (getMetadataValue(formatData as Record<string, unknown> | null | undefined, FD_MIMETYPE_SLUG) as string) || '';
  const rendersImageFromProxy = fullResolution && mimetype?.includes(IMAGE);
  // The thumbnail query existed before this change. We only skip it for full-resolution images;
  // videos and audio still use the thumbnail format as the player poster.
  const shouldLoadThumbnailFormat = !rendersImageFromProxy;

  const {
    data: thumbnailData,
    loading: loadingThumb,
    error: errorThumb,
  } = useQuery(shared ? buildSharedFormatsQ() : buildFormatsQ(), {
    skip: (!itemId && !itemObjectId) || !shouldLoadThumbnailFormat,
    variables: { ...getVariables(FORMAT), fmtType_Slug: THUMBNAIL_FORMAT_SLUG },
  });

  const thumbnailFormat: FormatData = getFirstNestedWithSaneDefault(thumbnailData, [{}])[0];

  const {
    data: metadata,
    loading: loadingMetadata,
    error: metadataError,
  } = useMetaQ(
    {
      skip: !itemId && !itemObjectId,
      variables: { ...getVariables(META), definitionSlugIn: [FD_NAME_SLUG, FD_TC_SLUG, FD_INPOINT_SLUG] },
    },
    shared,
  );

  if (loading || (shouldLoadThumbnailFormat && loadingThumb) || loadingFormatData || loadingMetadata) {
    return <LoadingMedia />;
  }

  if (error || (shouldLoadThumbnailFormat && errorThumb) || formatDataError || metadataError) {
    return (
      <ImageWrapperStyles>
        <BoxImageStyles>
          <MediaUnavailable text={MEDIA_ERROR} />
        </BoxImageStyles>
      </ImageWrapperStyles>
    );
  }

  if (!proxyFormat?.online) {
    return (
      <ImageWrapperStyles>
        <BoxImageStyles>
          <MediaUnavailable text={PROXY_OFFLINE} />
        </BoxImageStyles>
      </ImageWrapperStyles>
    );
  }

  const requiresOnlineThumbnail = shouldLoadThumbnailFormat && !mimetype?.includes(PDF);

  if (requiresOnlineThumbnail && !thumbnailFormat?.online) {
    return (
      <ImageWrapperStyles>
        <BoxImageStyles>
          <MediaUnavailable text={THUMBNAIL_OFFLINE} />
        </BoxImageStyles>
      </ImageWrapperStyles>
    );
  }

  const metadataRecord = metadata as Record<string, unknown> | null | undefined;
  const name = getMetadataValue(metadataRecord, 'name') as string | undefined;
  const thumbnailFmt = genMediaUrl(shared ? SHARED_MEDIA_PATH : MEDIA_PATH, thumbnailFormat?.id || '');
  const audioPoster = thumbnailFmt ? thumbnailFmt : AUDIO_IMAGE;
  const mediaPoster =
    mimetype?.includes(VIDEO) || mimetype?.includes(MXF) || mimetype?.includes(IMAGE)
      ? thumbnailFmt
      : mimetype?.includes(AUDIO)
        ? audioPoster
        : PDF_IMAGE;

  const id = itemId;
  const _timecode = getMetadataValue(metadataRecord, 'timecode') as string | undefined;
  const _inpoint = getMetadataValue(metadataRecord, 'inpoint') as string | undefined;

  const mediaUrl = genMediaUrl(shared ? SHARED_MEDIA_PATH : MEDIA_PATH, proxyFormat?.id || '');

  if (mimetype?.includes(VIDEO) || mimetype?.includes(AUDIO)) {
    return (
      <VideoJs
        enableKeyBindings={isVisualMode(viewStateVar().mode)}
        className="customRightClick"
        setPlayer={setPlayer as ((player: Player) => void) | undefined}
        asset={{ id }}
        audioPosterMode={mimetype?.includes(AUDIO)}
        videoUrl={mediaUrl}
        poster={mediaPoster}
        videoId={props.akomiMediaCardIndex?.toString()}
        preload="none"
        getVideoRef={getVideoRef as unknown as ((ref: React.RefObject<HTMLVideoElement>) => void) | undefined}
        marker={marker as { markIn?: string; markOut?: string } | undefined}
        {...videoJsProps}
      />
    );
  }

  if (mimetype?.includes(IMAGE)) {
    return (
      <ImageBox
        className="customRightClick"
        imageBoxFormat={fullResolution ? mediaUrl : thumbnailFmt}
        onClickHandler={props.onImageClick}
        name={name}
        unavailableText={fullResolution ? PROXY_OFFLINE : THUMBNAIL_OFFLINE}
      />
    );
  }

  if (mimetype?.includes(PDF)) {
    return <PDFThumbnail onClickHandler={props.onImageClick} cardImageSrc={PDF_IMAGE} />;
  }

  return (
    <ImageWrapperStyles>
      <BoxImageStyles>
        <MediaUnavailable text="Mimetype Not Supported" />
      </BoxImageStyles>
    </ImageWrapperStyles>
  );
};

export default MediaRouterComponent;
